const STORAGE_NAMESPACE = 'skillarea:one-time-modal:v1';
const STORAGE_VALUE = 'true';

export const WIN_REAL_MONEY_MODAL_MAX_BALANCE_CENTS = 500;
export const WIN_REAL_MONEY_MODAL_FREE_PLAY_GAMES_THRESHOLD = 3;

function getUtcDayKey(date: Date): number {
  return (
    date.getUTCFullYear() * 10000 +
    (date.getUTCMonth() + 1) * 100 +
    date.getUTCDate()
  );
}

function getTodayUtcKey(): number {
  return getUtcDayKey(new Date());
}

function getValidatedUserId(userId: number): string {
  if (!Number.isInteger(userId) || userId <= 0) {
    throw new Error('Valid userId is required for one-time modal storage');
  }

  return String(userId);
}

function getStorage() {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage;
}

function getGoPlayModalShownKey(userId: number): string {
  return `${STORAGE_NAMESPACE}:go-play:shown:${getValidatedUserId(userId)}`;
}

function getWinMoreModalShownKey(userId: number): string {
  return `${STORAGE_NAMESPACE}:win-more:shown:${getValidatedUserId(userId)}`;
}

function getWinMoreModalPendingKey(userId: number): string {
  return `${STORAGE_NAMESPACE}:win-more:pending-paid-win:${getValidatedUserId(
    userId,
  )}`;
}

function getWinMoreModalShownForDayKey(userId: number, dayKey: number): string {
  return `${STORAGE_NAMESPACE}:win-more:shown-day:${dayKey}:${getValidatedUserId(
    userId,
  )}`;
}

function getWinMoreModalPendingForDayKey(
  userId: number,
  dayKey: number,
): string {
  return `${STORAGE_NAMESPACE}:win-more:pending-day:${dayKey}:${getValidatedUserId(
    userId,
  )}`;
}

function getNonFreePlayFinishedCountForDayKey(
  userId: number,
  dayKey: number,
): string {
  return `${STORAGE_NAMESPACE}:matchmaking:finished-non-freeplay-count-day:${dayKey}:${getValidatedUserId(
    userId,
  )}`;
}

function getFreePlayFinishedCountForDayKey(
  userId: number,
  dayKey: number,
): string {
  return `${STORAGE_NAMESPACE}:matchmaking:finished-freeplay-count-day:${dayKey}:${getValidatedUserId(
    userId,
  )}`;
}

function getWinRealMoneyModalShownForDayKey(
  userId: number,
  dayKey: number,
): string {
  return `${STORAGE_NAMESPACE}:win-real-money:shown-day:${dayKey}:${getValidatedUserId(
    userId,
  )}`;
}

function getWinRealMoneyModalPendingForDayKey(
  userId: number,
  dayKey: number,
): string {
  return `${STORAGE_NAMESPACE}:win-real-money:pending-day:${dayKey}:${getValidatedUserId(
    userId,
  )}`;
}

function getChestUnlockedPendingForDayKey(
  userId: number,
  dayKey: number,
): string {
  return `${STORAGE_NAMESPACE}:chest-unlocked:pending-day:${dayKey}:${getValidatedUserId(
    userId,
  )}`;
}

export function hasSeenGoPlayModal(userId: number): boolean {
  const storage = getStorage();
  if (!storage) return false;

  return storage.getItem(getGoPlayModalShownKey(userId)) === STORAGE_VALUE;
}

export function markGoPlayModalSeen(userId: number): void {
  const storage = getStorage();
  if (!storage) return;

  storage.setItem(getGoPlayModalShownKey(userId), STORAGE_VALUE);
}

export function hasSeenWinMoreModal(userId: number): boolean {
  const storage = getStorage();
  if (!storage) return false;

  return storage.getItem(getWinMoreModalShownKey(userId)) === STORAGE_VALUE;
}

export function markWinMoreModalSeen(userId: number): void {
  const storage = getStorage();
  if (!storage) return;

  storage.setItem(getWinMoreModalShownKey(userId), STORAGE_VALUE);
}

export function hasPendingWinMoreModal(userId: number): boolean {
  const storage = getStorage();
  if (!storage) return false;

  return storage.getItem(getWinMoreModalPendingKey(userId)) === STORAGE_VALUE;
}

export function markWinMoreModalPending(userId: number): void {
  const storage = getStorage();
  if (!storage) return;

  if (hasSeenWinMoreModal(userId)) return;

  storage.setItem(getWinMoreModalPendingKey(userId), STORAGE_VALUE);
}

export function clearPendingWinMoreModal(userId: number): void {
  const storage = getStorage();
  if (!storage) return;

  storage.removeItem(getWinMoreModalPendingKey(userId));
}

export function incrementNonFreePlayFinishedGamesToday(
  userId: number,
): number | null {
  const storage = getStorage();
  if (!storage) return null;

  const todayKey = getTodayUtcKey();
  const key = getNonFreePlayFinishedCountForDayKey(userId, todayKey);
  const currentRaw = storage.getItem(key);
  const current = currentRaw ? Number(currentRaw) : 0;
  const next = Number.isFinite(current) && current > 0 ? current + 1 : 1;
  storage.setItem(key, String(next));
  return next;
}

export function incrementFreePlayFinishedGamesToday(
  userId: number,
): number | null {
  const storage = getStorage();
  if (!storage) return null;

  const todayKey = getTodayUtcKey();
  const key = getFreePlayFinishedCountForDayKey(userId, todayKey);
  const currentRaw = storage.getItem(key);
  const current = currentRaw ? Number(currentRaw) : 0;
  const next = Number.isFinite(current) && current > 0 ? current + 1 : 1;
  storage.setItem(key, String(next));
  return next;
}

export function hasSeenWinMoreModalToday(userId: number): boolean {
  const storage = getStorage();
  if (!storage) return false;
  const todayKey = getTodayUtcKey();
  return (
    storage.getItem(getWinMoreModalShownForDayKey(userId, todayKey)) ===
    STORAGE_VALUE
  );
}

export function markWinMoreModalSeenToday(userId: number): void {
  const storage = getStorage();
  if (!storage) return;
  const todayKey = getTodayUtcKey();
  storage.setItem(
    getWinMoreModalShownForDayKey(userId, todayKey),
    STORAGE_VALUE,
  );
}

export function hasPendingWinMoreModalToday(userId: number): boolean {
  const storage = getStorage();
  if (!storage) return false;
  const todayKey = getTodayUtcKey();
  return (
    storage.getItem(getWinMoreModalPendingForDayKey(userId, todayKey)) ===
    STORAGE_VALUE
  );
}

export function markWinMoreModalPendingToday(userId: number): void {
  const storage = getStorage();
  if (!storage) return;
  if (hasSeenWinMoreModalToday(userId)) return;
  const todayKey = getTodayUtcKey();
  storage.setItem(
    getWinMoreModalPendingForDayKey(userId, todayKey),
    STORAGE_VALUE,
  );
}

export function clearPendingWinMoreModalToday(userId: number): void {
  const storage = getStorage();
  if (!storage) return;
  const todayKey = getTodayUtcKey();
  storage.removeItem(getWinMoreModalPendingForDayKey(userId, todayKey));
}

export function hasSeenWinRealMoneyModalToday(userId: number): boolean {
  const storage = getStorage();
  if (!storage) return false;
  const todayKey = getTodayUtcKey();
  return (
    storage.getItem(getWinRealMoneyModalShownForDayKey(userId, todayKey)) ===
    STORAGE_VALUE
  );
}

export function markWinRealMoneyModalSeenToday(userId: number): void {
  const storage = getStorage();
  if (!storage) return;
  const todayKey = getTodayUtcKey();
  storage.setItem(
    getWinRealMoneyModalShownForDayKey(userId, todayKey),
    STORAGE_VALUE,
  );
}

export function hasPendingWinRealMoneyModalToday(userId: number): boolean {
  const storage = getStorage();
  if (!storage) return false;
  const todayKey = getTodayUtcKey();
  return (
    storage.getItem(getWinRealMoneyModalPendingForDayKey(userId, todayKey)) ===
    STORAGE_VALUE
  );
}

export function markWinRealMoneyModalPendingToday(userId: number): void {
  const storage = getStorage();
  if (!storage) return;
  if (hasSeenWinRealMoneyModalToday(userId)) return;
  const todayKey = getTodayUtcKey();
  storage.setItem(
    getWinRealMoneyModalPendingForDayKey(userId, todayKey),
    STORAGE_VALUE,
  );
}

export function clearPendingWinRealMoneyModalToday(userId: number): void {
  const storage = getStorage();
  if (!storage) return;
  const todayKey = getTodayUtcKey();
  storage.removeItem(getWinRealMoneyModalPendingForDayKey(userId, todayKey));
}

export function hasPendingChestUnlockedModalToday(userId: number): boolean {
  const storage = getStorage();
  if (!storage) return false;
  const todayKey = getTodayUtcKey();
  return (
    storage.getItem(getChestUnlockedPendingForDayKey(userId, todayKey)) ===
    STORAGE_VALUE
  );
}

export function markChestUnlockedModalPendingToday(userId: number): void {
  const storage = getStorage();
  if (!storage) return;
  const todayKey = getTodayUtcKey();
  storage.setItem(
    getChestUnlockedPendingForDayKey(userId, todayKey),
    STORAGE_VALUE,
  );
}

export function clearPendingChestUnlockedModalToday(userId: number): void {
  const storage = getStorage();
  if (!storage) return;
  const todayKey = getTodayUtcKey();
  storage.removeItem(getChestUnlockedPendingForDayKey(userId, todayKey));
}
